import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { of, from } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { SubscriptionService } from '../service/subscription.service';
import { SubscriptionApiService } from '../service/subscription-api.service';
import { CouponApiService } from '../../coupon/service/coupon-api.service';
import { ChargifyService } from '../../payment/service/chargify.service';
import { PaymentResult } from '../../payment/model/payment-result';
import { Coupon } from '../../coupon/model/coupon';
import {
  ValidateCoupon,
  ClearCoupon,
  SetSelectedProduct,
  PreviewSubscription,
  CreateSubscription,
  GenerateChargifyToken,
  ClearSubscriptionData,
} from './subscription.actions';
import { Product } from '../../product/model/product';
import { SubscriptionPreview } from '../model/subscription-preview';

export interface SubscriptionStateModel {
  products: Product[] | null;
  selectedProduct: Product | null;
  coupon: Coupon | null;
  preview: SubscriptionPreview | null;
  chargifyToken: string | null;
  loading: boolean;
  error: string | null;
  createSuccess: boolean;
}

const defaults: SubscriptionStateModel = {
  products: null,
  selectedProduct: null,
  coupon: null,
  preview: null,
  chargifyToken: null,
  loading: false,
  error: null,
  createSuccess: false,
};

@State<SubscriptionStateModel>({
  name: 'subscription',
  defaults,
})
@Injectable()
export class SubscriptionState {
  constructor(
    private subscriptionService: SubscriptionService,
    private subscriptionApiService: SubscriptionApiService,
    private couponApiService: CouponApiService,
    private chargifyService: ChargifyService
  ) {}

  @Action(ValidateCoupon)
  validateCoupon(
    ctx: StateContext<SubscriptionStateModel>,
    action: ValidateCoupon
  ) {
    ctx.patchState({ loading: true, error: null });

    return this.couponApiService
      .validateCoupon(action.couponCode, action.apiName)
      .pipe(
        tap({
          next: result => {
            if (result.valid) {
              const coupon: Coupon = {
                code: result.code || action.couponCode,
                amount: result.amount,
                name: result.name,
                amountInCents: result.amountInCents,
                description: result.description,
                percentage: result.percentage,
                valid: result.valid,
              };
              ctx.patchState({ coupon, loading: false });
            } else {
              ctx.patchState({
                coupon: null,
                loading: false,
                error: result.message || 'Invalid coupon code',
              });
            }
          },
          error: error => {
            const errorMessage = error.message || 'Failed to validate coupon';
            ctx.patchState({
              coupon: null,
              loading: false,
              error: errorMessage,
            });
          },
        }),
        catchError(error => {
          const errorMessage = error.message || 'Failed to validate coupon';
          ctx.patchState({
            coupon: null,
            loading: false,
            error: errorMessage,
          });

          return of(null);
        })
      );
  }

  @Action(ClearCoupon)
  clearCoupon(ctx: StateContext<SubscriptionStateModel>) {
    ctx.patchState({ coupon: null });
  }

  @Action(SetSelectedProduct)
  setSelectedProduct(
    ctx: StateContext<SubscriptionStateModel>,
    action: SetSelectedProduct
  ) {
    ctx.patchState({ selectedProduct: action.selectedProduct });
  }

  @Action(PreviewSubscription)
  previewSubscription(
    ctx: StateContext<SubscriptionStateModel>,
    action: PreviewSubscription
  ) {
    ctx.patchState({ loading: true, error: null });

    return this.subscriptionService
      .previewSubscription(
        action.productName,
        action.couponCode,
        action.referralCode
      )
      .pipe(
        tap({
          next: preview => {
            const state = ctx.getState();
            const stateCoupon = state.coupon;

            if (
              stateCoupon &&
              stateCoupon.amount &&
              (!preview.coupon || preview.coupon.amount !== stateCoupon.amount)
            ) {
              const couponDisplay = stateCoupon.code ?? 'Unknown Coupon';
              const updatedPreview = {
                ...preview,
                currentTotalBillingAmount: Math.max(
                  0,
                  preview.currentSubTotalBillingAmount - stateCoupon.amount
                ),
                coupon: {
                  amount: stateCoupon.amount,
                  name: couponDisplay,
                },
              };
              ctx.patchState({ preview: updatedPreview, loading: false });
            } else {
              // Clear coupon information from preview when there's no coupon in state
              const updatedPreview = {
                ...preview,
                currentTotalBillingAmount: preview.currentSubTotalBillingAmount,
                coupon: null,
              };
              ctx.patchState({ preview: updatedPreview, loading: false });
            }
          },
        })
      );
  }

  @Action(CreateSubscription)
  createSubscription(
    ctx: StateContext<SubscriptionStateModel>,
    action: CreateSubscription
  ) {
    ctx.patchState({ loading: true, error: null, createSuccess: false });

    return this.subscriptionApiService.createSubscription(action.request).pipe(
      tap({
        next: () => {
          ctx.patchState({ loading: false, createSuccess: true, error: null });
        },
        error: error => {
          const errorMessage = error.message || 'Failed to create subscription';

          ctx.patchState({
            loading: false,
            error: errorMessage,
            createSuccess: false,
          });
        },
      }),
      catchError(error => {
        const errorMessage = error.message || 'Failed to create subscription';

        ctx.patchState({
          loading: false,
          error: errorMessage,
          createSuccess: false,
        });

        return of(null);
      })
    );
  }

  @Action(GenerateChargifyToken)
  generateChargifyToken(
    ctx: StateContext<SubscriptionStateModel>,
    action: GenerateChargifyToken
  ) {
    ctx.patchState({ loading: true, error: null });

    return from(
      this.chargifyService.getToken(action.formElement, action.billingAddress)
    ).pipe(
      tap({
        next: (result: PaymentResult) => {
          if (result.success && result.token) {
            ctx.patchState({ chargifyToken: result.token, loading: false });
          } else {
            ctx.patchState({
              chargifyToken: null,
              loading: false,
              error: result.error || 'Failed to generate token',
            });
          }
        },
        error: error => {
          const errorMessage =
            error.message || 'Failed to generate Chargify token';
          ctx.patchState({
            chargifyToken: null,
            loading: false,
            error: errorMessage,
          });
        },
      }),
      catchError(error => {
        const errorMessage =
          error.message || 'Failed to generate Chargify token';
        ctx.patchState({
          chargifyToken: null,
          loading: false,
          error: errorMessage,
        });

        return of(null);
      })
    );
  }

  @Action(ClearSubscriptionData)
  clearData(ctx: StateContext<SubscriptionStateModel>) {
    ctx.setState(defaults);
  }
}
